import { useState } from "react";
import { makeGetApiCall, makePostApiCall } from "../../shared/makeApiCall";
import ModelErrorComponent from "../../components/modals/ModelErrorComponent";

export function TraceabilityViewModel(logout) {
  const [emptyModal, setEmptyModelOpen] = useState({
    open: false,
    childComponent: null,
  });
  const [loading, setLoading] = useState(false);

  // Step state
  const [lookup, setLookup] = useState("");
  const [err, setErr] = useState("");

  // Resolved current WIP context
  const [currentWip, setCurrentWip] = useState(null); // { id, lot_number }
  const [incomingPlus, setIncomingPlus] = useState([]); // [{ plu, incoming_kg, incoming_pcs, parent_wip_count }]

  // Chosen PLU within the current WIP (picked once)
  const [selectedPlu, setSelectedPlu] = useState(null);

  // Detail for chosen PLU (first level only)
  const [byParent, setByParent] = useState([]);
  const [byLot, setByLot] = useState([]);

  // Breadcrumbs for drill
  const [crumbs, setCrumbs] = useState([]); // [{ wip_id, label }]

  // (kept for backward compatibility views)
  const [directParents, setDirectParents] = useState([]);

  // Supplier item tracing state (Trace Children)
  const [lotNumber, setLotNumber] = useState(null);
  const [source, setSource] = useState(null); // Single source object
  const [directChildren, setDirectChildren] = useState([]);
  // Legacy receiving state for backward compatibility
  const [receiving, setReceiving] = useState(null);

  const doLookup = async function (raw, reportType = 1) {
    setLoading(true);
    setErr("");

    // Reset state based on report type
    if (reportType === 1) {
      // Manufactured product tracing
      setReceiving(null);
      setDirectChildren([]);
      let callResult = await makeGetApiCall(
        `trace/v2/${encodeURIComponent(raw)}`,
      );
      if (callResult.success) {
        setCurrentWip(callResult.data.wip || null);
        setIncomingPlus(callResult.data.incoming_plus || []);
        setLoading(false);
        return {
          ok: true,
          wip: callResult.data.wip,
          incoming_plus: callResult.data.incoming_plus || [],
        };
      } else {
        setLoading(false);
        setErr(callResult.data.message || "Lookup failed");
        setCurrentWip(null);
        setIncomingPlus([]);
        setCrumbs([]);
        if (callResult.errorStatus === 401) logout();
        return { ok: false, error: callResult.data.message || "Lookup failed" };
      }
    } else if (reportType === 2) {
      // Supplier item tracing
      setCurrentWip(null);
      setIncomingPlus([]);
      setCrumbs([]);
      setSelectedPlu(null);
      setByParent([]);
      setByLot([]);

      return await traceSupplierItem(raw);
    }
  };

  const loadPluDetails = async (wipId, plu) => {
    setErr("");
    setLoading(true);
    setByParent([]);
    setByLot([]);
    setDirectParents([]);
    let callResult = await makeGetApiCall(
      `trace/v2/${wipId}/plu/${encodeURIComponent(plu)}`,
    );
    setLoading(false);
    if (callResult.success) {
      const by_parent = callResult.data.by_parent || [];
      const by_lot = callResult.data.by_lot || [];
      setByParent(by_parent);
      setByLot(by_lot);
      return { ok: true, by_parent, by_lot }; // <-- return the arrays
    } else {
      const msg = callResult.data.message || "Failed to load PLU details";
      setErr(msg);
      return { ok: false, error: msg };
    }
  };

  const loadDirectParents = async (wipId) => {
    setErr("");
    setLoading(true);
    setDirectParents([]);
    let callResult = await makeGetApiCall(`trace/v2/${wipId}/parents`);
    setLoading(false);
    if (callResult.success) {
      const parents = callResult.data.parents || [];
      setDirectParents(parents);
      return { ok: true, parents }; // <-- return the array
    } else {
      const msg = callResult.data.message || "Failed to load parents";
      setErr(msg);
      return { ok: false, error: msg };
    }
  };

  // ── NEW: raw fetchers for lazy tree building (no shared state changes)
  const fetchPluParents = async (wipId, plu) => {
    const r = await makeGetApiCall(
      `trace/v2/${wipId}/plu/${encodeURIComponent(plu)}`,
    );
    if (!r.success)
      throw new Error(r.data?.message || "Failed to fetch PLU parents");
    return r.data; // { by_parent, by_lot }
  };

  const fetchDirectParents = async (wipId) => {
    const r = await makeGetApiCall(`trace/v2/${wipId}/parents`);
    if (!r.success)
      throw new Error(r.data?.message || "Failed to fetch parents");
    return r.data.parents; // array
  };

  // Supplier item tracing functions (Trace Children)
  const traceSupplierItem = async function (raw) {
    setLoading(true);
    setErr("");
    setLotNumber(null);
    setSource(null);
    setDirectChildren([]);
    setReceiving(null);

    const callResult = await makeGetApiCall(
      `trace/children/v2/${encodeURIComponent(raw)}`,
    );

    if (callResult.success) {
      // { lot_number, source: { is_wip, ... }, direct_children[] }
      const data = callResult.data;

      // Parse numeric values (API may return strings)
      let parsedSource = null;
      if (data.source) {
        const sourceObj = data.source;
        if (sourceObj.is_wip) {
          parsedSource = {
            ...sourceObj,
            wip_id: parseInt(sourceObj.wip_id, 10) || 0,
          };
        } else {
          const num = (v) => (v == null || v === "" ? undefined : Number(v));

          const parseReceivingLine = (line) => ({
            receiving_invoice_id: Number(line.receiving_invoice_id) || 0,
            receiving_invoice_number: line.receiving_invoice_number,
            receiving_lot_number: line.receiving_lot_number,
            total_received_kg: num(line.total_received_kg),
            total_received_lbs: num(line.total_received_lbs),
            total_received_pcs: num(line.total_received_pcs),
            receiving_carton_count: num(line.receiving_carton_count),
            receiving_date: line.receiving_date ?? null,
            supplier_name: line.supplier_name ?? null,
            warehouse_name: line.warehouse_name ?? null,
            warehouse_location_name: line.warehouse_location_name ?? null,
            supplier_id:
              line.supplier_id == null ? null : Number(line.supplier_id),
          });

          parsedSource = {
            is_wip: false,
            receiving_invoices: (sourceObj.receiving_invoices || []).map(
              parseReceivingLine,
            ),
            shipped_items: (sourceObj.shipped_items || []).map((item) => ({
              id: item.id,
              order_id: item.order_id,
              product_name: item.product_name,
              lot_number: item.lot_number,
              total_in_kg: parseFloat(item.total_in_kg) || 0,
              total_in_lbs: parseFloat(item.total_in_lbs) || 0,
              total_in_pieces: item.total_in_pieces ?? 0,
              order: {
                id: item.order?.id,
                po_number: item.order?.po_number,
                customer_name: item.order?.customer_name,
              },
            })),
          };
        }
      }

      const directChildren = (data.direct_children || []).map((child) => ({
        ...child,
        used_kg: parseFloat(child.used_kg) || 0,
        used_pcs: parseFloat(child.used_pcs) || 0,
        child_total_produced_kg: parseFloat(child.child_total_produced_kg) || 0,
        child_total_produced_pcs: parseInt(child.child_total_produced_pcs) || 0,
      }));

      setLotNumber(data.lot_number || raw);
      setSource(parsedSource);
      setDirectChildren(directChildren);
      setLoading(false);
      return {
        ok: true,
        lot_number: data.lot_number || raw,
        source: parsedSource,
        direct_children: directChildren,
      };
    } else {
      setLoading(false);
      const errorMsg = callResult.data.message || "Lot number lookup failed";
      setErr(errorMsg);
      if (callResult.errorStatus === 401) logout();
      return { ok: false, error: errorMsg };
    }
  };

  const loadDirectChildren = async (wipId) => {
    // Don't set global loading state for lazy loading - component handles its own loading
    const callResult = await makeGetApiCall(
      `trace/children/v2/${wipId}/children`,
    );
    if (callResult.success) {
      // Parse numeric values (API may return strings)
      const children = (callResult.data.children || []).map((child) => ({
        ...child,
        used_kg: parseFloat(child.used_kg) || 0,
        used_pcs: parseFloat(child.used_pcs) || 0,
        child_total_produced_kg: parseFloat(child.child_total_produced_kg) || 0,
        child_total_produced_pcs: parseInt(child.child_total_produced_pcs) || 0,
      }));
      return { ok: true, children };
    } else {
      const msg = callResult.data.message || "Failed to load children";
      if (callResult.errorStatus === 401) logout();
      return { ok: false, error: msg };
    }
  };

  const loadChildrenTree = async (wipId, maxDepth = 10) => {
    setErr("");
    setLoading(true);
    const callResult = await makeGetApiCall(
      `trace/children/v2/${wipId}/tree?maxDepth=${maxDepth}`,
    );
    setLoading(false);
    if (callResult.success) {
      return { ok: true, tree: callResult.data };
    } else {
      const msg = callResult.data.message || "Failed to load tree";
      setErr(msg);
      if (callResult.errorStatus === 401) logout();
      return { ok: false, error: msg };
    }
  };

  const exportTraceability = async function (lot, reportType) {
    const callResult = await makePostApiCall(`exportTraceability/${lot}`, {
      type: reportType,
    });
    if (callResult.success) {
      window.open(callResult.data.location, "_blank").focus();
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal("Could not trace carton");
    }
  };

  const getSalesReport = async function (lotNumber) {
    if (!lotNumber) {
      showErrorModal("No lot number available");
      return;
    }

    const callResult = await makeGetApiCall(
      `traceSoldCartonsByLot/${lotNumber}`,
    );
    if (callResult.success) {
      if (callResult.data?.location) {
        window.open(callResult.data.location, "_blank").focus();
      } else {
        showErrorModal("Failed to get sales report location");
      }
    } else {
      if (callResult.errorStatus === 401) logout();
      showErrorModal("Could not get sales report");
    }
  };

  const showErrorModal = (subtitle) => {
    setEmptyModelOpen({
      childComponent: (
        <ModelErrorComponent
          setOpen={setEmptyModelOpen}
          subtitle={subtitle}
          title="Error"
        />
      ),
      open: true,
    });
  };

  return {
    // core
    doLookup,
    loading,
    exportTraceability,
    getSalesReport,
    emptyModal,
    setEmptyModelOpen,
    lookup,
    setLookup,
    err,

    // context
    currentWip,
    setCurrentWip,
    incomingPlus,

    // selection
    selectedPlu,
    setSelectedPlu,

    // first-level detail
    loadPluDetails,
    byParent,
    byLot,

    // breadcrumbs
    crumbs,
    setCrumbs,

    // legacy (kept)
    loadDirectParents,
    directParents,

    // new lazy fetchers
    fetchPluParents,
    fetchDirectParents,

    // Supplier item tracing (Trace Children)
    lotNumber,
    source,
    directChildren,
    loadDirectChildren,
    loadChildrenTree,
    // Legacy receiving for backward compatibility
    receiving,
  };
}
